/**
 * The page's own tools the account page uses (part 36c, FX4): copying text to the clipboard, and printing what is marked
 * for it (the backup codes). Thin adapters over the browser's objects, made once at the start and handed to the app.
 */

/** The clipboard, as far as used (`navigator.clipboard`). */
export interface ClipboardLike {
  writeText(text: string): Promise<void>;
}

/** FX4: copies text with `clipboard` (none in an insecure page or an old browser): true once it is there. */
export function clipboardCopier(clipboard: ClipboardLike | undefined): (text: string) => Promise<boolean> {
  return async (text) => {
    if (clipboard === undefined) return false;
    try {
      await clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };
}

/** What a share sheet is handed (`ShareData`'s fields the invite uses). */
export interface SharedText {
  readonly title: string;
  readonly text: string;
  readonly url: string;
}

/** The browser's share sheet, as far as the invite link uses it (`navigator`). */
export interface ShareSheetLike {
  share?: (data: SharedText) => Promise<void>;
}

/**
 * Part 38b, IW1: the device's share sheet over `navigator` (true once the writer shared; false when they closed it or it
 * failed), or null where the browser has none, so Share… shows only where it works.
 */
export function shareSheetOf(navigator: ShareSheetLike): ((data: SharedText) => Promise<boolean>) | null {
  const { share } = navigator;
  if (share === undefined) return null;
  return async (data) => {
    try {
      await share.call(navigator, data);
      return true;
    } catch {
      return false;
    }
  };
}

/** The page's window, as far as printing uses it. */
export interface PrintingWindow {
  print(): void;
  readonly document: { readonly documentElement: HTMLElement };
}

/**
 * FX4: the print dialog of `page` with `mark` set on its root element while it is open, so the page's print style shows
 * only what is marked (`[data-printing]` in `style.css`); `print` blocks until the dialog closes.
 */
export function printer(page: PrintingWindow, mark: string): () => void {
  return () => {
    const root = page.document.documentElement;
    root.dataset['printing'] = mark;
    try {
      page.print();
    } finally {
      delete root.dataset['printing'];
    }
  };
}
